'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible } from '../../../../components/collapsible';
import { MetaDefault } from '../../../../components/typography';
import { type TracePhase, type TraceToolCall } from '../types';
import { getToolDisplayName } from '../utils/tool-config';
import { TraceExpandChevron } from './TraceExpandChevron';
import { StandardToolCard } from './StandardToolCard';

type TraceMcpToolCardGroupProps = {
  toolCalls: TraceToolCall[];
  tracePhase?: TracePhase;
  preferHarOverToolOutput?: boolean;
  onHideMessage?: () => void;
};

export function TraceMcpToolCardGroup({
  toolCalls,
  tracePhase = 'ai-discovery',
  preferHarOverToolOutput,
  onHideMessage,
}: TraceMcpToolCardGroupProps) {
  const t = useTranslations('designSystem.chat');
  const [isOpen, setIsOpen] = useState(false);

  if (toolCalls.length === 0) {
    return null;
  }

  if (toolCalls.length === 1 && toolCalls[0]) {
    return (
      <StandardToolCard
        toolCall={toolCalls[0]}
        displayName={getToolDisplayName(
          toolCalls[0].name,
          toolCalls[0].args,
          tracePhase,
        )}
        tracePhase={tracePhase}
        preferHarOverToolOutput={preferHarOverToolOutput}
        showWithoutHar
        onHideMessage={onHideMessage}
      />
    );
  }

  const headerLabel = t('mcpTool.multipleCalls', { count: toolCalls.length });

  return (
    <VStack alignItems="stretch" gap={1} width="100%">
      <Collapsible.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
        unmountOnExit
      >
        <Collapsible.Trigger asChild>
          <HStack
            w="full"
            gap={1.5}
            alignItems="center"
            py={1}
            cursor="pointer"
            borderRadius="sm"
          >
            <MetaDefault color="fg.muted">{headerLabel}</MetaDefault>

            <TraceExpandChevron
              isExpanded={isOpen}
              tracePhase={tracePhase}
              onHideMessage={onHideMessage}
            />
          </HStack>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <VStack alignItems="stretch" gap={2} pt={3} width="100%">
            {toolCalls.map((toolCall) => (
              <Box key={toolCall.id} width="100%">
                <StandardToolCard
                  toolCall={toolCall}
                  displayName={getToolDisplayName(
                    toolCall.name,
                    toolCall.args,
                    tracePhase,
                  )}
                  tracePhase={tracePhase}
                  preferHarOverToolOutput={preferHarOverToolOutput}
                  showWithoutHar
                />
              </Box>
            ))}
          </VStack>
        </Collapsible.Content>
      </Collapsible.Root>
    </VStack>
  );
}
